import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import ReplicateLimitLabel from 'studies/typed/shared/procedure-runs/conditions/ReplicateLimitLabel';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import stopPropagation from 'util/stopPropagation';

import {BaseCondition, Condition, ConditionGroup} from '../design-state/useExperimentDesignState';
import {
  CONDITIONS_LIMIT_MESSAGE,
  isAtConditionsLimit,
  MAX_CONDITIONS_PER_PROCEDURE_RUN,
  MethodExecutionLimitState,
} from '../execution-status/useMethodExecutionLimitState';
import AddConditionsModal from './AddConditionsModal';
import AddPlannedConditionsModal from './AddPlannedConditionsModal';
import {
  appendPlanConditionRows,
  buildConditionsWithAddedGroups,
  getDefaultReplicateCountForNewCondition,
} from './conditionUtils';

/**
 * Callback invoked when the user picks "Add multiple …" from the dropdown. The shell hands the
 * variant its own `addConditions(numConditions, numReplicates)` so each flavor can open its
 * preferred bulk-add modal (`AddPlannedConditionsModal` for plans, `AddConditionsModal` for
 * runs) and wire the modal's submit through to this same callback.
 */
export type OpenBulkAddModal = (onSubmit: (numConditions: number, numReplicates: number) => void) => void;

type BuildAddedConditions<TCondition extends BaseCondition> = (
  conditions: Array<TCondition>,
  args: {numConditions: number; numReplicates: number}
) => {conditions: Array<TCondition>; prevIndexes: Array<number | null>};

export interface EditNumConditionGroupsButtonsProps<TCondition extends BaseCondition> {
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
  /** Whether to render the inline `ReplicateLimitLabel` next to the buttons. */
  showReplicateLimitLabel: boolean;
  /**
   * Variant-specific: whether the at-limit state disables further adds. Plans use a
   * condition-count limit; runs use a replicate-count limit.
   */
  isAtLimit: boolean;
  /** Variant-specific: tooltip text shown when {@link isAtLimit} is true. */
  limitTooltipText: string;
  /** Variant-specific: opens the bulk-add modal. See {@link OpenBulkAddModal}. */
  openBulkAddModal: OpenBulkAddModal;
  /**
   * Variant-specific: appends new rows and returns the matching `prevIndexes`. Plans append plain
   * rows with no coverage map; runs append groups that record the methods they cover.
   */
  buildAddedConditions: BuildAddedConditions<TCondition>;
}

/**
 * Shared add/remove buttons for condition rows. Plans and runs supply limit predicates, bulk-add
 * modals, and row builders via {@link planConditionGroupsButtonVariant} /
 * {@link runConditionGroupsButtonVariant}.
 */
export function EditNumConditionGroupsButtons<TCondition extends BaseCondition>(
  props: EditNumConditionGroupsButtonsProps<TCondition>
): JSX.Element {
  function addConditions(numConditions: number, numReplicates: number): void {
    const {conditions, prevIndexes} = props.buildAddedConditions(props.conditions, {
      numConditions,
      numReplicates,
    });
    props.setConditions(conditions, prevIndexes);
  }

  const lastNumReplicates = getDefaultReplicateCountForNewCondition(props.conditions);

  return (
    <div className={css(styles.buttonContainer)}>
      <div
        className={css(styles.button)}
        // Embedded in a table header that toggles collapse on double-click. Prevent that
        // bubbling so rapid button clicks don't accidentally collapse/expand the table.
        onDoubleClick={stopPropagation}
      >
        <Tooltip text={props.limitTooltipText} isDisabled={!props.isAtLimit}>
          <ButtonGroup>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              icon="compose"
              size={ButtonSize.MINI}
              onClick={() => {
                addConditions(1, lastNumReplicates);
              }}
              disabled={props.isAtLimit}
              id="add-condition-button"
            />
            <Dropdown
              menu={
                <Menu width={MenuWidth.SMALL}>
                  <Option
                    dataTestAttrs={{
                      component: 'AddConditionsDropdown',
                      element: 'addMultipleConditionsButton',
                    }}
                    onSelect={() => {
                      props.openBulkAddModal(addConditions);
                    }}
                  >
                    {`Add multiple ${getProcedureRunConditionTextForProcedureType(props.procedureType, {
                      plural: true,
                    })}`}
                  </Option>
                </Menu>
              }
            >
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="chevron-down"
                iconAltText="Show dropdown menu"
                dataTestAttrs={{component: 'AddConditionsDropdown', element: 'addMultipleConditionsDropdown'}}
                disabled={props.isAtLimit}
                id="add-multiple-conditions-dropdown-button"
              />
            </Dropdown>
          </ButtonGroup>
        </Tooltip>
      </div>
      {props.showReplicateLimitLabel && (
        <ReplicateLimitLabel replicateLimit={props.methodExecutionLimitState.replicateLimit} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  button: {paddingTop: '3px'},
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '12px',
    marginLeft: 12,
  },
});

type VariantProps<TCondition extends BaseCondition> = Pick<
  EditNumConditionGroupsButtonsProps<TCondition>,
  'isAtLimit' | 'limitTooltipText' | 'openBulkAddModal' | 'buildAddedConditions'
>;

/**
 * Returns the plan-flavored variant props for {@link EditNumConditionGroupsButtons}: the
 * planned-condition limit predicate / tooltip, a bulk-add modal that opens
 * {@link AddPlannedConditionsModal}, and a builder that appends plain plan rows.
 */
export function planConditionGroupsButtonVariant({
  conditions,
  methodExecutionLimitState,
  procedureType,
}: {
  conditions: ReadonlyArray<Condition>;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
}): VariantProps<Condition> {
  return {
    isAtLimit: isAtConditionsLimit(methodExecutionLimitState),
    limitTooltipText: `Cannot exceed ${MAX_CONDITIONS_PER_PROCEDURE_RUN} planned conditions`,
    openBulkAddModal: (onSubmit) => {
      legacyRunModal(AddPlannedConditionsModal, {
        onRequestSubmit: ({numConditions, numReplicates}) => {
          onSubmit(numConditions, numReplicates);
        },
        remainingConditions: MAX_CONDITIONS_PER_PROCEDURE_RUN - conditions.length,
        procedureType,
      });
    },
    buildAddedConditions: (currentConditions, {numConditions, numReplicates}) =>
      appendPlanConditionRows(currentConditions, numConditions, numReplicates, procedureType),
  };
}

/**
 * Returns the run-flavored variant props for {@link EditNumConditionGroupsButtons}: the
 * replicate-count limit predicate / tooltip, a bulk-add modal that opens
 * {@link AddConditionsModal}, and a builder that appends groups with `conditionIdsByPmdvId`.
 */
export function runConditionGroupsButtonVariant({
  conditions,
  methodExecutionLimitState,
  procedureType,
  newConditionCoveredMethodIds,
}: {
  conditions: ReadonlyArray<ConditionGroup>;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
}): VariantProps<ConditionGroup> {
  const {replicateLimit, remainingReplicates} = methodExecutionLimitState;
  const lastNumReplicates = getDefaultReplicateCountForNewCondition(conditions);
  const isAtConditionLimit = isAtConditionsLimit(methodExecutionLimitState);

  return {
    isAtLimit: isAtConditionLimit || remainingReplicates === 0,
    limitTooltipText: isAtConditionLimit
      ? CONDITIONS_LIMIT_MESSAGE
      : `Cannot exceed ${replicateLimit} ${pluralize(replicateLimit, 'replicate')}`,
    openBulkAddModal: (onSubmit) => {
      legacyRunModal(AddConditionsModal, {
        onRequestSubmit: ({numConditions, numReplicates}) => {
          onSubmit(numConditions, numReplicates);
        },
        remainingReplicates,
        replicateLimit,
        initialReplicates: lastNumReplicates,
        remainingConditions: MAX_CONDITIONS_PER_PROCEDURE_RUN - conditions.length,
        procedureType,
      });
    },
    buildAddedConditions: (currentConditions, {numConditions, numReplicates}) =>
      buildConditionsWithAddedGroups(currentConditions, {
        numConditions,
        numReplicates,
        procedureType,
        coveredMethodIds: newConditionCoveredMethodIds,
      }),
  };
}
